import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { formatNum } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  HeartPulse, Building2, Users, ShieldCheck, Cpu, Database, Timer, UploadCloud, RefreshCw, Bell, GitBranch,
} from "lucide-react";
import { PageShell, Stat } from "./PageShell";

const DOT = { red: "bg-red-500", amber: "bg-amber-400", green: "bg-[#00E65B]", grey: "bg-muted-foreground" };

const BUILD_BADGE = { shipped: "success", fix: "warning", docs: "outline", ops: "outline" };

function LatestBuildCard() {
  const [build, setBuild] = useState(null);
  useEffect(() => {
    fetch("/build-log.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : []))
      .then((entries) => {
        if (!entries.length) return;
        const latest = [...entries].sort((a, b) => {
          const [am, ai2] = String(a.build).split(".").map(Number);
          const [bm, bi] = String(b.build).split(".").map(Number);
          return am - bm || (ai2 || 0) - (bi || 0);
        }).pop();
        setBuild(latest);
      })
      .catch(() => {});
  }, []);
  if (!build) return null;
  return (
    <div className="card-flat p-4 border-l-2 border-l-accent" data-testid="latest-build-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="label-eyebrow flex items-center gap-2">
          <GitBranch className="h-3.5 w-3.5 text-accent" /> Latest build — what changed in this deploy
        </div>
        <a href="/build-log.html" target="_blank" rel="noreferrer"
          className="text-[11px] text-accent hover:underline font-mono" data-testid="build-log-link">
          Full build log →
        </a>
      </div>
      <div className="flex items-baseline gap-3 mt-2 flex-wrap">
        <span className="font-mono font-bold text-accent" data-testid="latest-build-number">Build {build.build}</span>
        <span className="text-sm font-semibold">{build.title}</span>
        <Badge variant={BUILD_BADGE[build.status] || "outline"} className="text-[9px] uppercase">{build.status}</Badge>
        <span className="text-[11px] text-muted-foreground font-mono">{build.date} · {build.tested}</span>
      </div>
      <ul className="mt-2 space-y-1">
        {(build.changes || []).map((c, i) => (
          <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-accent shrink-0">·</span>{c}</li>
        ))}
      </ul>
    </div>
  );
}

function uptime(s) {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}

export default function PlatformHealth() {
  const [d, setD] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    Promise.all([api.get("/platform/health"), api.get("/platform/audit")])
      .then(([h, a]) => { setD(h.data); setAudit((a.data.entries || []).slice(0, 6)); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <PageShell title="Platform Health" testid="platform-health-page"><div className="text-sm text-muted-foreground">Loading telemetry…</div></PageShell>;

  const { infra, fleet, users, ai, imports_30d: imp, notifications, volume, alerts } = d;
  const subs = fleet.subscriptions || {};

  return (
    <PageShell
      title="Platform Health" testid="platform-health-page"
      subtitle="Fleet and infrastructure telemetry across all Lestari tenants — counts, statuses and sizes only; tenant business data is never surfaced here."
      actions={<Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="health-refresh-btn"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh</Button>}
    >
      {/* Latest build */}
      <LatestBuildCard />

      {/* Alerts */}
      <div className="card-flat p-4" data-testid="health-alerts">
        <div className="label-eyebrow mb-2 flex items-center gap-2"><HeartPulse className="h-3.5 w-3.5 text-accent" /> Fleet alerts</div>
        <div className="space-y-1.5">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`alert-${a.severity}-${i}`}>
              <span className={`h-2 w-2 rounded-full shrink-0 ${DOT[a.severity] || DOT.grey}`} />{a.text}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Tenants" value={fleet.onboarded} sub={`${fleet.total} total · ${fleet.pending} pending · ${fleet.suspended} suspended`} tone="accent" />
        <Stat icon={Users} label="Users" value={users.total} sub={`${users.active_7d} active 7d · ${users.active_30d} active 30d`} />
        <Stat icon={ShieldCheck} label="MFA adoption" value={`${users.mfa_adoption_pct}%`} sub={`${users.mfa_enabled} of ${users.total} enrolled · ${fleet.mfa_policy_on} tenant polic${fleet.mfa_policy_on === 1 ? "y" : "ies"} enforced`} tone={users.mfa_adoption_pct >= 50 ? "accent" : "amber"} />
        <Stat icon={Cpu} label="AI calls (7d)" value={formatNum(ai.calls_7d)} sub={`${formatNum(ai.calls_30d)} in 30d · S$${formatNum(ai.est_cost_30d_sgd, { decimals: 2 })} est. cost`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Database} label="DB latency" value={`${infra.db_ping_ms} ms`} sub={`${formatNum(infra.db_objects)} objects · ${infra.db_collections} collections`} tone={infra.db_ping_ms > 100 ? "amber" : undefined} />
        <Stat icon={Database} label="DB storage" value={`${formatNum(infra.db_storage_mb, { decimals: 1 })} MB`} sub={`${formatNum(infra.db_data_mb, { decimals: 1 })} MB data · ${formatNum(infra.db_indexes)} indexes`} />
        <Stat icon={Timer} label="API uptime" value={uptime(infra.api_uptime_seconds)} sub="since last backend restart" />
        <Stat icon={UploadCloud} label="Import success (30d)" value={`${imp.success_rate_pct}%`} sub={`${imp.total} runs · ${imp.failed} failed`} tone={imp.failed ? "amber" : undefined} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Fleet status */}
        <div className="card-flat p-4" data-testid="health-fleet-card">
          <div className="label-eyebrow mb-3">Subscription fleet</div>
          <div className="space-y-2">
            {[["active", "Paid — active", "success"], ["trialing", "Trialing", "warning"], ["expired", "Trial expired", "destructive"], ["none", "No subscription", "outline"]].map(([k, label, variant]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{label}</span>
                <Badge variant={variant} className="font-mono">{subs[k] || 0}</Badge>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm border-t border-border pt-2">
              <span className="text-muted-foreground">Suspended by operator</span>
              <Badge variant={fleet.suspended ? "destructive" : "outline"} className="font-mono">{fleet.suspended}</Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1"><Bell className="h-3 w-3" /> Notifications 30d / unread</span>
              <span className="font-mono text-sm">{notifications.sent_30d} / {notifications.unread}</span>
            </div>
          </div>
        </div>

        {/* AI top features */}
        <div className="card-flat p-4" data-testid="health-ai-card">
          <div className="label-eyebrow mb-3">AI usage — top features (30d)</div>
          {ai.top_features.length === 0 ? <div className="text-sm text-muted-foreground">No AI calls recorded in the last 30 days.</div> : (
            <div className="space-y-2">
              {ai.top_features.map((f) => {
                const pct = Math.round(f.calls / (ai.top_features[0].calls || 1) * 100);
                return (
                  <div key={f.feature}>
                    <div className="flex justify-between text-xs mb-0.5"><span className="truncate">{f.feature}</span><span className="font-mono text-muted-foreground">{f.calls}</span></div>
                    <div className="h-1.5 bg-surface-hover rounded-sm"><div className="h-1.5 bg-accent rounded-sm" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
              <div className="text-[11px] text-muted-foreground pt-1">{formatNum(ai.tokens_30d)} tokens · S${formatNum(ai.est_cost_30d_sgd, { decimals: 2 })} estimated cost</div>
            </div>
          )}
        </div>

        {/* Data volume */}
        <div className="card-flat p-4" data-testid="health-volume-card">
          <div className="label-eyebrow mb-3">Data volume (all tenants)</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {volume.map((v) => (
              <div key={v.key} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground truncate">{v.label}</span>
                <span className="font-mono">{formatNum(v.count)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent operator actions */}
      <div className="card-flat p-4" data-testid="health-audit-card">
        <div className="label-eyebrow mb-2">Recent operator actions</div>
        {audit.length === 0 ? <div className="text-sm text-muted-foreground">No operator actions recorded yet.</div> : (
          <div className="space-y-1.5">
            {audit.map((a) => (
              <div key={a.id} className="flex items-center gap-3 text-xs">
                <span className="font-mono text-muted-foreground shrink-0">{new Date(a.at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <Badge variant="outline" className="text-[9px] uppercase shrink-0">{a.action.replace(/_/g, " ")}</Badge>
                <span className="truncate">{a.target || ""} {a.detail ? `— ${a.detail}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
